import { FC, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import SendIcon from '@material-ui/icons/Send';
import { useTranslation } from 'react-i18next';
import { extractRequirementTexts, hasCsvHeader } from './csv';
import type { ImportMode } from './types';

const MAX_REQUIREMENTS = 100;

interface RegisterFormProps {
  /** Registers the parsed texts in the chosen mode. Returns count on success. */
  onRegister: (texts: string[], mode: ImportMode) => Promise<number | null>;
  /** Navigate back to the list (this form lives on its own Import screen). */
  onBack: () => void;
}

const useStyles = makeStyles(theme => ({
  mb2: { marginBottom: theme.spacing(2) },
  dropzone: {
    border: '2px dashed',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(4),
    textAlign: 'center',
    cursor: 'pointer',
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
  dropzoneActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
  },
  dropzoneInactive: {
    borderColor: theme.palette.divider,
    backgroundColor: 'transparent',
  },
  uploadIcon: { fontSize: 40, marginBottom: theme.spacing(1) },
  approvedPanel: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 3,
    padding: theme.spacing(1.25, 1.5),
    backgroundColor: theme.palette.action.hover,
    marginBottom: theme.spacing(2),
  },
  defaultHint: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    marginLeft: 6,
  },
  submit: { marginTop: theme.spacing(1) },
}));

const RegisterForm: FC<RegisterFormProps> = ({ onRegister, onBack }) => {
  const classes = useStyles();
  const { t } = useTranslation('requirement-lifecycle');

  const [activeTab, setActiveTab] = useState(0);
  const [textInput, setTextInput] = useState('');
  const [csvFileName, setCsvFileName] = useState<string | null>(null);
  const [csvTexts, setCsvTexts] = useState<string[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [importApproved, setImportApproved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Texts for the active tab, normalized (trimmed, blanks dropped).
  const texts = useMemo(() => {
    if (activeTab === 0) {
      return textInput
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);
    }
    return csvTexts;
  }, [activeTab, textInput, csvTexts]);

  const handleCsvFile = async (file: File) => {
    try {
      setError(null);
      if (!file.name.toLowerCase().endsWith('.csv')) {
        setError(t('register.validation.invalidFileType'));
        return;
      }
      const content = await file.text();
      if (!hasCsvHeader(content)) {
        setError(t('register.validation.missingTitleColumn'));
        setCsvFileName(file.name);
        setCsvTexts([]);
        return;
      }
      const parsed = extractRequirementTexts(content);
      if (parsed.length === 0) {
        setError(t('register.validation.empty'));
        setCsvFileName(file.name);
        setCsvTexts([]);
        return;
      }
      if (parsed.length > MAX_REQUIREMENTS) {
        setError(t('register.validation.tooMany'));
        setCsvFileName(file.name);
        setCsvTexts([]);
        return;
      }
      setCsvFileName(file.name);
      setCsvTexts(parsed);
    } catch (err) {
      setError(t('common.error', { detail: String(err) }));
    }
  };

  const handleSubmit = async () => {
    if (texts.length === 0) {
      setError(t('register.validation.empty'));
      return;
    }
    if (texts.length > MAX_REQUIREMENTS) {
      setError(t('register.validation.tooMany'));
      return;
    }
    setLoading(true);
    setError(null);
    const mode: ImportMode = importApproved ? 'approved' : 'draft';
    try {
      const count = await onRegister(texts, mode);
      if (count !== null) {
        // Clear the consumed input; the parent owns the success toast + refresh.
        setTextInput('');
        setCsvFileName(null);
        setCsvTexts([]);
        // Return to the list so the newly imported rows are visible.
        onBack();
      }
    } finally {
      setLoading(false);
    }
  };

  let submitLabel: string;
  if (loading) {
    submitLabel = t('register.submitting');
  } else if (importApproved) {
    submitLabel = t('register.submitApproved', { count: texts.length });
  } else {
    submitLabel = t('register.submitDraft', { count: texts.length });
  }

  return (
    <Card className={classes.mb2}>
      <CardContent>
        <Button size="small" onClick={onBack} className={classes.mb2}>
          {t('register.back')}
        </Button>
        <Typography variant="h6" className={classes.mb2}>
          {t('s01.registerTitle')}
        </Typography>

        {error && (
          <Alert
            severity="error"
            className={classes.mb2}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <Tabs
          value={activeTab}
          onChange={(_e, v) => setActiveTab(v)}
          className={classes.mb2}
        >
          <Tab label={t('s01.tabs.text')} />
          <Tab label={t('s01.tabs.csv')} />
        </Tabs>

        {activeTab === 0 && (
          <Box>
            <TextField
              fullWidth
              multiline
              rows={6}
              value={textInput}
              onChange={e => setTextInput(e.target.value)}
              placeholder={t('register.textPlaceholder')}
              label={t('register.textLabel')}
              variant="outlined"
              className={classes.mb2}
              disabled={loading}
            />
          </Box>
        )}

        {activeTab === 1 && (
          <Box className={classes.mb2}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: 'none' }}
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) void handleCsvFile(file);
                // Reset value so selecting the same file again triggers onChange.
                e.target.value = '';
              }}
              data-testid="csv-file-input"
            />
            <Box
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => {
                e.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={e => {
                e.preventDefault();
                setDragActive(false);
              }}
              onDrop={e => {
                e.preventDefault();
                setDragActive(false);
                const file = e.dataTransfer.files?.[0];
                if (file) void handleCsvFile(file);
              }}
              className={`${classes.dropzone} ${
                dragActive ? classes.dropzoneActive : classes.dropzoneInactive
              }`}
            >
              <CloudUploadIcon className={classes.uploadIcon} />
              <Typography variant="body2" color="textSecondary">
                {csvFileName
                  ? t('register.csvDetected', {
                      file: csvFileName,
                      count: csvTexts.length,
                    })
                  : t('register.csvPlaceholder')}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {t('register.csvHint')}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Import as Approved toggle — shared across both tabs. */}
        <Box className={classes.approvedPanel}>
          <FormControlLabel
            control={
              <Switch
                checked={importApproved}
                onChange={e => setImportApproved(e.target.checked)}
                color="primary"
                disabled={loading}
              />
            }
            label={
              <span>
                {t('register.importApproved.label')}
                <span className={classes.defaultHint}>
                  {t('register.importApproved.default')}
                </span>
              </span>
            }
          />
          {importApproved && (
            <Alert severity="warning" style={{ marginTop: 8 }}>
              {t('register.importApproved.warning')}
            </Alert>
          )}
        </Box>

        <Button
          variant="contained"
          color="primary"
          fullWidth
          className={classes.submit}
          endIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
          onClick={handleSubmit}
          disabled={loading || texts.length === 0}
        >
          {submitLabel}
        </Button>
      </CardContent>
    </Card>
  );
};

export default RegisterForm;
